function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
import React, { useState } from 'react';
import { motion } from 'motion/react';
import produce from 'immer';
import partition from 'lodash/partition';
import camelCase from 'lodash/camelCase';
import { EntrySectionUpdates, FiltersOperator, MetricEntryBehavior, QueryFilterEntry, RowLabelType, SelectorTypeDefinitions } from 'common/query-builder/query-entry-section/types';
import { Event, EventItem } from 'common/query-builder/query-block/events-menu/types';
import { MetricType } from 'common/types/reports/bookmark';
import { QueryBlockReact } from 'common/query-builder/query-block';
import { MenuType } from 'common/query-builder/query-block/types';
import { ButtonTheme } from 'common/components/button/constants';
import { SelectorType as QueryBlockSelectorType } from 'common/query-builder/query-block/constants';
import { OverflowControls } from 'common/query-builder/query-entry-section/row-types/new-metric-entry/common/controls';
import { ControlBarAction, ControlBarActionItem, ControlBarActionItems } from 'common/query-builder/query-entry-section/constants';
import { ButtonReact } from 'common/components/button';
import { createFirstTimeEventFilter, isFirstTimeEventFilter } from 'common/widgets/property-filter-menu/models/utils';
// eslint-disable-next-line import/no-cycle
import { QueryEntrySectionReact } from 'common/query-builder/query-entry-section';
import { getFilterEntriesFromFilters } from 'iron/common/query-builder/query-entry-section/row-types/filter/utils';
import { PropertyFilter } from 'common/widgets/property-filter-menu/models/property-filter';
import { getEventNamesFromEntries } from 'iron/common/query-builder/query-entry-section/row-types/row-util';
import { RowFiltersPrefix } from 'common/query-builder/query-entry-section/components/filters';
import { isXauMathType } from 'common/query-builder/query-builder/util';
import { BEHAVIOR_PROPERTIES } from 'common/query-builder/query-block/properties-menu/constants';
import { SvgIconReact } from 'common/components/svg-icon';
import type { InheritedEntryRowProps, RowProps } from 'iron/common/query-builder/query-entry-section/row-types/new-metric-entry/types/catalog';
import { SVG_ICONS } from 'common/components/svg-icon/icons';
import { PropertySectionItem } from 'common/query-builder/query-block/properties-menu/item-menu-types';
import styles from './index.module.styl';
type EventRowProps = RowProps & {
  additionalOverflowControls?: ControlBarActionItem[];
  behavior: MetricEntryBehavior;
  /** renders a label inside the row icon indicator */
  iconLabel?: string | null;
  /** overrides the selected event name */
  label?: string | null;
  onClose?: (() => void) | null | undefined;
  onDelete?: (() => void) | null | undefined;
  onDuplicate?: (() => void) | null | undefined;
  onSelect?: ((behavior: MetricEntryBehavior) => void) | null | undefined;
  /** renders a label before the event name */
  prefixLabel?: string | null;
  /**
   * Open the query block for event selection.
   */
  openQueryBlock?: boolean;
  /**
   * Show this icon for the row icon.
   */
  rowIcon?: keyof typeof SVG_ICONS | null;
  /** renders a label after the event name */
  suffixLabel?: string | null;

  /** hides all overflow controls for a given event */
  hideControls?: boolean;

  /** Enables cohort filters */
  enableCohortFilters?: boolean;

  /** Injects custom filter options */
  otherFilterOptions?: Array<PropertySectionItem<string>>;
};

/**
 * Updates filters for a given behavior along with the filter determiner
 */
function handleFiltersUpdate(props: {
  behavior: MetricEntryBehavior;
  filters?: PropertyFilter[];
  updatedFiltersOperator?: FiltersOperator | null;
  onSelect: EventRowProps[`onSelect`];
}) {
  const result = produce(props.behavior, behavior => {
    var _props$filters;
    behavior.filters = (_props$filters = props.filters) !== null && _props$filters !== void 0 ? _props$filters : props.behavior.filters;
    if (props.updatedFiltersOperator) {
      behavior.filtersDeterminer = props.updatedFiltersOperator === FiltersOperator.And ? "all" : "any";
    }
  });
  props.onSelect(result);
}
export function RowFilters(props: InheritedEntryRowProps & {
  behavior: MetricEntryBehavior;
  dataGroupId?: string | null;
  disableCloneEntry?: boolean;
  disabledItems?: Obj;
  eventNames?: string[];
  hideEvents?: boolean;
  enableCohortFilters?: boolean;
  isStaging?: boolean;
  onAbortStage: () => void;
  onSelect: EventRowProps[`onSelect`];
  otherFilterOptions?: EventRowProps[`otherFilterOptions`];
}) {
  var _props$sectionProps$p, _props$eventNames, _props$hideEvents, _ref2, _props$otherFilterOpt, _props$disabledItems, _props$disableCloneEn;
  const filtersOperator = props.behavior.filtersDeterminer === "all" ? FiltersOperator.And : FiltersOperator.Or;
  const [firstTimeFilters, regularFilters] = partition(props.behavior.filters, isFirstTimeEventFilter);

  /** renders the AND/OR filter operator selector */
  const conjunctionRenderer = _ref => {
    let {
      index: filterIndex,
      entry: filterEntry
    } = _ref;
    return filterIndex === 0 && regularFilters.length > 1 && <RowFiltersPrefix filterIndex={filterIndex} filter={new PropertyFilter((filterEntry as QueryFilterEntry).event)} filtersOperator={props.behavior.filtersDeterminer === "all" ? FiltersOperator.And : FiltersOperator.Or} isReadOnlyMode={false} forNewUI handleChange={item => handleFiltersUpdate({
      behavior: props.behavior,
      updatedFiltersOperator: item.value as FiltersOperator,
      onSelect: props.onSelect
    })} />;
  };
  return <QueryEntrySectionReact containedSection indentFilters elref={camelCase("entry-filters")} autoactivatePlaceholders enableAllDataGroupsForScdProps={props.sectionProps.enableAllDataGroupsForScdProps} excludeDimensionProperties={props.sectionProps.excludeDimensionProperties} includeCustomProperties={props.sectionProps.includeCustomProperties} showBorrowedProperties={props.sectionProps.showBorrowedProperties} hideEditControls={props.sectionProps.hideEditControls} propertyCombineMode={(_props$sectionProps$p = props.sectionProps.propertyCombineMode) !== null && _props$sectionProps$p !== void 0 ? _props$sectionProps$p : undefined} eventNames={(_props$eventNames = props.eventNames) !== null && _props$eventNames !== void 0 ? _props$eventNames : []} chartType={props.currentChartType} supportB2bCohorts={props.sectionProps.dataGroupsEnabled} dataGroupsEnabled={props.sectionProps.dataGroupsEnabled} projectId={props.sectionProps.projectId} workspaceId={props.sectionProps.workspaceId} dataGroupId={props.dataGroupId} disableAddEntry rowTypes={[SelectorTypeDefinitions.Filter]} rowLabelType={RowLabelType.None} hideEvents={(_props$hideEvents = props.hideEvents) !== null && _props$hideEvents !== void 0 ? _props$hideEvents : false} otherFilterOptions={(_ref2 = (_props$otherFilterOpt = props.otherFilterOptions) !== null && _props$otherFilterOpt !== void 0 ? _props$otherFilterOpt : props.sectionProps.otherFilterOptions) !== null && _ref2 !== void 0 ? _ref2 : []} projectHasCohorts={props.enableCohortFilters} projectHasPeople disabledItems={(_props$disabledItems = props.disabledItems) !== null && _props$disabledItems !== void 0 ? _props$disabledItems : {}} rowDraggable trackProperties={{
    sectionName: "inline-filter"
  }} disableCloneEntry={(_props$disableCloneEn = props.disableCloneEntry) !== null && _props$disableCloneEn !== void 0 ? _props$disableCloneEn : false} entries={getFilterEntriesFromFilters([...regularFilters, ...firstTimeFilters], filtersOperator)} sectionName={"inline-filter"} renderSuffix={regularFilters.length > 1 ? conjunctionRenderer : undefined} onUpdate={(updates: EntrySectionUpdates<QueryFilterEntry>) => {
    handleFiltersUpdate({
      behavior: props.behavior,
      filters: updates.entries.map(entry => entry.event as PropertyFilter),
      onSelect: props.onSelect
    });
  }} stagingRow={props.isStaging} onAbortStage={() => props.onAbortStage()} data-sentry-element="QueryEntrySectionReact" data-sentry-component="RowFilters" data-sentry-source-file="index.tsx" />;
}

/**
 * Provides a component for rendering a behavior event selection together with filters.
 *
 * @todo(metrics) filters are not matching designs
 */
export function BehaviorEventRow(props: EventRowProps) {
  var _props$rowIcon, _props$openQueryBlock, _ref3, _props$entry$measurem;
  const [entryEl, setEntryEl] = useState<HTMLDivElement>(null);
  const [isStagingFilter, setIsStagingFilter] = useState(false);
  const hasFirstTimeFilter = props.behavior.filters.some(isFirstTimeEventFilter);
  const handleAddFilter = () => setIsStagingFilter(true);
  const handleDelete = () => props.onDelete();
  const handleDuplicate = () => props.onDuplicate();
  const handleEventSelected = (selectedEvent: EventItem) => {
    const result = produce(props.behavior, behavior => {
      var _selectedEvent$id;
      behavior.name = selectedEvent.value;
      behavior.id = (_selectedEvent$id = selectedEvent.id) !== null && _selectedEvent$id !== void 0 ? _selectedEvent$id : null;
      behavior.type = selectedEvent.custom ? MetricType.CustomEvent : MetricType.Event;
    });
    setIsStagingFilter(false);
    props.onSelect(result);
  };
  const handleAddFirstTimeFilter = () => !hasFirstTimeFilter && handleFiltersUpdate({
    filters: [...props.behavior.filters, createFirstTimeEventFilter()],
    behavior: props.behavior,
    onSelect: props.onSelect
  });
  return <motion.div ref={setEntryEl} className={styles.eventRow} whileHover='hovered' variants={{
    hovered: {
      opacity: 1
    }
  }} data-sentry-element="unknown" data-sentry-component="BehaviorEventRow" data-sentry-source-file="index.tsx">
      <motion.div className={styles.header} variants={{
      hovered: {
        paddingLeft: "4px"
      }
    }} transition={{
      type: "spring",
      visualDuration: 0.1,
      bounce: 0.3
    }} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
        <div className={styles.eventIndex}>
          <SvgIconReact className={styles.icon} icon={(_props$rowIcon = props.rowIcon) !== null && _props$rowIcon !== void 0 ? _props$rowIcon : "behavior-event"} data-sentry-element="SvgIconReact" data-sentry-source-file="index.tsx" />
          {props.iconLabel}
        </div>

        {/* renders the actual event selection menu */}
        <QueryBlockReact className={styles.content} type={MenuType.Events} showSessionEvent selectorIcon='' selectorType={QueryBlockSelectorType.Button} theme={ButtonTheme.QueryBuilder} label={props.label} disabled={props.sectionProps.disabled} userId={props.sectionProps.userId} workspaceId={props.sectionProps.workspaceId} projectId={props.sectionProps.projectId} prefixLabel={props.prefixLabel} suffixLabel={props.suffixLabel} contextPanelParentElement={entryEl} selected={{
        label: null,
        id: props.behavior.id,
        value: props.behavior.name,
        custom: props.behavior.type === MetricType.CustomEvent
      } satisfies Event} onClose={() => {
        var _props$onClose;
        return (_props$onClose = props.onClose) === null || _props$onClose === void 0 ? void 0 : _props$onClose.call(props);
      }} onSelect={detail => handleEventSelected(detail.item)} open={(_props$openQueryBlock = props.openQueryBlock) !== null && _props$openQueryBlock !== void 0 ? _props$openQueryBlock : false} data-sentry-element="QueryBlockReact" data-sentry-source-file="index.tsx" />

        {/* overflow controls for each individual event in a behavior */}
        {!props.hideControls && <div className={styles.actions}>
            <ButtonReact theme={ButtonTheme.Tertiary} icon='filter-xs' onClick={handleAddFilter} />
            {props.onDelete && <ButtonReact theme={ButtonTheme.Tertiary} icon='x' onClick={handleDelete} />}
            <OverflowControls items={[{
          items: [/** stage filter */_objectSpread(_objectSpread({}, ControlBarActionItems[ControlBarAction.Filter]), {}, {
            onSelect: handleAddFilter
          }), /** events can only have one first time filter */
          !hasFirstTimeFilter && _objectSpread(_objectSpread({}, ControlBarActionItems[ControlBarAction.FirstTimeFilter]), {}, {
            onSelect: handleAddFirstTimeFilter
          }), /** delete event */
          props.onDelete && _objectSpread(_objectSpread({}, ControlBarActionItems[ControlBarAction.Delete]), {}, {
            onSelect: handleDelete
          }), /** duplicate event */
          props.onDuplicate && _objectSpread(_objectSpread({}, ControlBarActionItems[ControlBarAction.Duplicate]), {}, {
            onSelect: handleDuplicate
          }), /** passes in any extract overflow action */
          ...(props.additionalOverflowControls || [])].filter(Boolean)
        }]} />
          </div>}
      </motion.div>

      {/* renders filters for the behavior event */}
      <div className={styles.filters}>
        <RowFilters {...props} behavior={props.behavior} isStaging={isStagingFilter}
      /** prevents behavioral or computed filters from showing up */ otherFilterOptions={props.otherFilterOptions} eventNames={getEventNamesFromEntries(props.entry)} enableCohortFilters={props.enableCohortFilters} disabledItems={isXauMathType(props.entry.measurement.aggregationOperator) ? Array.from(BEHAVIOR_PROPERTIES.keys()).reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {}) : {}} dataGroupId={(_ref3 = (_props$entry$measurem = props.entry.measurement.dataGroupId) !== null && _props$entry$measurem !== void 0 ? _props$entry$measurem : props.entry.behavior.dataGroupId) !== null && _ref3 !== void 0 ? _ref3 : props.sectionProps.dataGroupId} onAbortStage={() => setIsStagingFilter(false)} onSelect={props.onSelect} data-sentry-element="RowFilters" data-sentry-source-file="index.tsx" />
      </div>
    </motion.div>;
}